import { useCallback, useContext, useRef } from 'react'
import { v4 as uuid } from 'uuid'
import moment from 'moment'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'
import GeoContext from 'contexts/geoContext'

interface Options {
  offer: App.HotelOffer
  rooms: Array<App.Occupants>
  flightOrigin?: string
  enabled: boolean
  fetchCombined: boolean
  packages: Array<App.HotelPackage>
  combinedPackages: Array<App.HotelPackage>
  bypassRegionFilter?: boolean
  lereGroup?: string
}

/** Owns search invalidation before calendar action caches decide whether to fetch. */
function useFlashHotelCalendarRequests({ offer, rooms, flightOrigin, enabled, fetchCombined, packages, combinedPackages, bypassRegionFilter, lereGroup }: Options) {
  const dispatch = useAppDispatch()
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const searchKey = JSON.stringify([offer.id, flightOrigin, rooms.map(generateOccupancyStringByRoom), fetchCombined, lereGroup, bypassRegionFilter, currentRegionCode, currentCurrency])
  const lastRequestedSearch = useRef<string | undefined>(undefined)
  const requestScope = useRef<string | undefined>(undefined)
  const hasSearchChanged = useCallback(() => lastRequestedSearch.current !== searchKey, [searchKey])
  const requestCalendars = useCallback(() => {
    if (!enabled || !offer.packages.length || !rooms.length || !isValidOccupancy(...rooms)) return

    // Combined calendars are indexed by package, so they must be invalidated for
    // the full party, including repeated occupancies. Individual-only searches
    // retain their occupancy cache and fetch new types.
    if (fetchCombined && lastRequestedSearch.current !== undefined && lastRequestedSearch.current !== searchKey) {
      dispatch(clearBookingCalendar())
    }
    if (lastRequestedSearch.current !== searchKey) requestScope.current = uuid()
    const minDate = offer.bookingMinLeadDays != null
      ? moment().add(offer.bookingMinLeadDays, 'days').format('YYYY-MM-DD')
      : undefined
    const options = { occupants: rooms, flightOrigin, packages, minDate, bypassRegionFilter, lereGroup }
    dispatch(fetchCalendarsByOccupancy(offer.id, options))
    if (fetchCombined) {
      dispatch(fetchCalendars(offer.id, { ...options, packages: nonNullable([offer.lowestPricePackage]), requestScope: requestScope.current }))
      dispatch(fetchCalendars(offer.id, { ...options, packages: combinedPackages, requestScope: requestScope.current }))
    }
    lastRequestedSearch.current = searchKey
  }, [bypassRegionFilter, combinedPackages, dispatch, enabled, fetchCombined, flightOrigin, lereGroup, offer, packages, rooms, searchKey])

  return { requestCalendars, hasSearchChanged }
}

export default useFlashHotelCalendarRequests
